import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogActions, Button, Box, Typography,
  CircularProgress, IconButton, List, ListItemButton, ListItemText, Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';

const CURRENT = 'current';

const formatDateTime = (value) => new Date(value).toLocaleString('es-MX', { dateStyle: 'long', timeStyle: 'short' });

const formatSize = (bytes) => (bytes ? `${(bytes / 1024).toFixed(0)} KB` : '');

// Muestra el PDF de la cotización (GET /quotations/{id}/pdf): si ya se envió es la copia exacta del
// último PDF que recibió el cliente; si no, el mismo PDF generado con los datos actuales. Si hay
// más de un envío, el panel lateral permite abrir cada PDF tal cual se envió (GET /pdf-history).
const QuotationPreview = ({ open, onClose, quotationId }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [fileName, setFileName] = useState('cotizacion.pdf');
  const [sentAt, setSentAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [selected, setSelected] = useState(CURRENT);
  // La vista inicial (/pdf) puede ser la copia del último envío (SENT) o generada al vuelo (LIVE).
  const [currentIsSent, setCurrentIsSent] = useState(false);

  useEffect(() => {
    if (!open || !quotationId) return undefined;
    let cancelled = false;
    setSelected(CURRENT);
    setHistory([]);
    api.get(`/quotations/${quotationId}/pdf-history`)
      .then((res) => { if (!cancelled) setHistory(Array.isArray(res.data) ? res.data : []); })
      .catch(() => { if (!cancelled) setHistory([]); });
    return () => { cancelled = true; };
  }, [open, quotationId]);

  useEffect(() => {
    if (!open || !quotationId) return undefined;
    let url = null;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPdfUrl(null);
    const endpoint = selected === CURRENT
      ? `/quotations/${quotationId}/pdf`
      : `/quotations/${quotationId}/pdf-history/${selected}`;
    api.get(endpoint, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const match = /filename="([^"]+)"/.exec(res.headers?.['content-disposition'] || '');
        setFileName(match ? match[1] : `COT-${quotationId}_cotizacion.pdf`);
        const isSent = res.headers?.['x-pdf-source'] === 'SENT';
        setSentAt(isSent ? res.headers?.['x-pdf-sent-at'] || null : null);
        if (selected === CURRENT) setCurrentIsSent(isSent);
        setPdfUrl(url);
      })
      .catch((err) => {
        console.error('Error al obtener el PDF de la cotización:', err);
        if (!cancelled) setError('No se pudo obtener el PDF de la cotización');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [open, quotationId, selected]);

  const handleDownload = () => {
    if (!pdfUrl) return;
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleOpenInNewTab = () => {
    if (pdfUrl) window.open(pdfUrl, '_blank', 'noopener');
  };

  if (!open) return null;

  const showHistory = history.length > 1 || (history.length === 1 && !currentIsSent);

  return (
    <Dialog open={open} onClose={onClose} maxWidth={showHistory ? 'lg' : 'md'} fullWidth
      PaperProps={{ sx: { height: '92vh', borderRadius: 3 } }}>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1 }}>
        <Typography variant="subtitle2" sx={{ color: '#1a237e', fontWeight: 700 }}>
          {fileName.replace(/_cotizacion\.pdf$/, '')}
          <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>
            {loading ? '' : sentAt
              ? `PDF enviado al cliente el ${formatDateTime(sentAt)}`
              : 'Generado con los datos actuales (sin copia de envío)'}
          </Typography>
        </Typography>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex' }}>
        {showHistory && (
          <Box sx={{ width: 260, flexShrink: 0, borderRight: '1px solid #e0e0e0', overflowY: 'auto' }}>
            <Typography variant="overline" sx={{ px: 2, pt: 1, display: 'block', color: 'text.secondary' }}>
              Envíos al cliente ({history.length})
            </Typography>
            <Divider />
            <List dense disablePadding>
              {!currentIsSent && (
                <ListItemButton selected={selected === CURRENT} onClick={() => setSelected(CURRENT)}>
                  <ListItemText primary="Vista actual (sin enviar)"
                    secondary="Generada con los datos actuales"
                    primaryTypographyProps={{ fontSize: 13, fontWeight: selected === CURRENT ? 700 : 500 }}
                    secondaryTypographyProps={{ fontSize: 11 }} />
                </ListItemButton>
              )}
              {history.map((h, index) => {
                const isSelected = selected === h.id || (selected === CURRENT && index === 0 && currentIsSent);
                return (
                  <ListItemButton key={h.id} selected={isSelected}
                    onClick={() => setSelected(h.id)}>
                    <ListItemText
                      primary={`${index === 0 ? 'Último envío' : `Envío ${history.length - index}`} · ${formatDateTime(h.sentAt)}`}
                      secondary={[h.sentTo, h.sentBy ? `por ${h.sentBy}` : null, formatSize(h.sizeBytes)]
                        .filter(Boolean).join(' · ')}
                      primaryTypographyProps={{ fontSize: 13, fontWeight: isSelected ? 700 : 500 }}
                      secondaryTypographyProps={{ fontSize: 11, sx: { wordBreak: 'break-all' } }}
                    />
                  </ListItemButton>
                );
              })}
            </List>
          </Box>
        )}
        {loading ? (
          <Box display="flex" flex={1} justifyContent="center" alignItems="center" flexDirection="column" gap={1}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">Cargando PDF…</Typography>
          </Box>
        ) : error ? (
          <Box p={3}><Typography color="error">{error}</Typography></Box>
        ) : pdfUrl ? (
          <Box component="iframe" src={pdfUrl} title="Cotización PDF"
            sx={{ flex: 1, width: '100%', height: '100%', border: 0 }} />
        ) : null}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={handleOpenInNewTab} disabled={!pdfUrl} startIcon={<OpenInNewIcon />}
          sx={{ color: '#1a237e' }}>
          Abrir en pestaña
        </Button>
        <Button onClick={handleDownload} disabled={!pdfUrl} startIcon={<DownloadIcon />} variant="outlined"
          sx={{ borderColor: '#1a237e', color: '#1a237e' }}>
          Descargar PDF
        </Button>
        <Button onClick={onClose} variant="contained" sx={{ backgroundColor: '#1a237e' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuotationPreview;
